import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Loader2, Inbox, X, Send,
  PanelRightOpen, PanelRightClose, Sparkles, Reply, ReplyAll,
  Calendar, FolderPlus, Download, FileText, ChevronDown,
  Paperclip, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { auth } from '../../utils/auth';
import { cn } from '../../lib/utils';
import InboxTriagePanelV2 from './InboxTriagePanelV2';
import EmailComposeModal from './EmailComposeModal';
import MeetingBookingModal from './MeetingBookingModal';
import { EmailListPanel, EmailDetailPanel, AttachmentPreviewModal } from './components';
import { useInboxData, useEmailActions, useAttachmentPreview } from './hooks';
import { formatEmailTime, filterEmails, calculateInboxStats, STORAGE_KEYS, loadFromStorage, saveToStorage } from './utils';
import { useTimeTracking } from '../../contexts/TimeTrackingContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface SplitInboxViewProps {
  onNavigateToCase?: (caseId: string) => void;
}

/**
 * SPLIT INBOX VIEW - Refactored with hooks
 * 
 * Layout:
 * - Left: Compact email list with AI tags (resizable)
 * - Right: Full email content (100% height)
 * - AI Drawer: Slides in from right when needed
 */
const SplitInboxView: React.FC<SplitInboxViewProps> = ({ onNavigateToCase }) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['inbox', 'common']);
  const { startContextTracking, isTracking, currentContext } = useTimeTracking();
  
  // Use custom hooks for data and actions
  const {
    loading,
    syncing,
    outlookStatus,
    emails,
    threads,
    cases,
    clients,
    setEmails,
    setThreads,
    fetchData,
    refreshEmailsQuietly,
    syncFromOutlook,
    syncFromOutlookQuietly,
    userHasSelectedRef
  } = useInboxData();
  
  const {
    isReplying,
    replyMode,
    replyBody,
    replyTone,
    sendingReply,
    generatingAI,
    startReply,
    cancelReply,
    setReplyBody,
    setReplyTone,
    generateAIReply,
    sendReply,
    markAsRead,
    assignToCase,
    isAssigning
  } = useEmailActions({ onRefresh: refreshEmailsQuietly });
  
  const {
    previewAttachment,
    attachmentContent,
    docxHtml,
    loadingAttachment,
    previewAttachmentHandler,
    downloadAttachment,
    closePreview
  } = useAttachmentPreview();
  
  // Selection state
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedThread, setSelectedThread] = useState(null);
  const [expandedThread, setExpandedThread] = useState(null);
  
  // Filter state
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [triageView, setTriageView] = useState('triage');
  const [inboxTab, setInboxTab] = useState('all');
  
  // Modal state
  const [composeModalOpen, setComposeModalOpen] = useState(false);
  const [meetingModalOpen, setMeetingModalOpen] = useState(false);
  const [showCaseSelector, setShowCaseSelector] = useState(false);
  
  // AI Drawer state
  const [aiDrawerOpen, setAiDrawerOpen] = useState(true);
  const [aiDrawerPinned, setAiDrawerPinned] = useState(false);
  const [aiDrawerWidth, setAiDrawerWidth] = useState(() => loadFromStorage(STORAGE_KEYS.AI_DRAWER_WIDTH, 360));
  const [isResizingDrawer, setIsResizingDrawer] = useState(false);
  
  // Panel resize state
  const [panelWidth, setPanelWidth] = useState(() => loadFromStorage(STORAGE_KEYS.PANEL_WIDTH, 320));
  const [isResizingH, setIsResizingH] = useState(false);
  
  // Inline images for email display
  const [inlineImages, setInlineImages] = useState({});
  
  // Refs
  const containerRef = useRef(null);
  const replyEditorRef = useRef(null);
  
  // Fetch inline images when email changes
  useEffect(() => {
    const fetchInlineImages = async () => {
      if (!selectedEmail?.id || !selectedEmail?.body_html) return;
      
      // Check if body contains CID references
      const cidMatches = selectedEmail.body_html.match(/src=["']cid:([^"']+)["']/gi);
      if (!cidMatches || cidMatches.length === 0) {
        setInlineImages({});
        return;
      }
      
      try {
        const token = auth.getToken();
        const messageId = selectedEmail.outlook_message_id || selectedEmail.id;
        
        // Use dedicated inline-images endpoint
        const response = await axios.get(
          `${API}/outlook/emails/${messageId}/inline-images`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const imageMap = response.data?.inline_images || {};
        
        if (Object.keys(imageMap).length > 0) {
          setInlineImages(imageMap);
          console.log(`Loaded ${Object.keys(imageMap).length} inline images`);
        } else {
          setInlineImages({});
        }
      } catch (error) {
        console.log('Could not fetch inline images:', error.message);
        setInlineImages({});
      }
    };
    
    fetchInlineImages();
  }, [selectedEmail?.id, selectedEmail?.body_html, selectedEmail?.outlook_message_id]);
  
  // Process email HTML to replace CID references with actual image data
  const processEmailHtml = (html) => {
    if (!html) return '';
    
    let processedHtml = html;
    
    // Replace CID references with data URLs if we have them
    Object.entries(inlineImages).forEach(([cid, dataUrl]) => {
      // Clean the CID (remove angle brackets if present)
      const cleanCid = cid.replace(/[<>]/g, '');
      
      // Handle various CID formats in the HTML
      const patterns = [
        new RegExp(`src=["']cid:${cleanCid}["']`, 'gi'),
        new RegExp(`src=["']cid:<${cleanCid}>["']`, 'gi'),
        new RegExp(`src=["']cid:${cleanCid.replace('@', '%40')}["']`, 'gi'),
      ];
      
      patterns.forEach(pattern => {
        processedHtml = processedHtml.replace(pattern, `src="${dataUrl}"`);
      });
    });
    
    // Hide any remaining CID images that we couldn't resolve (shows nothing instead of broken icon)
    processedHtml = processedHtml
      .replace(/src="cid:[^"]*"/gi, 'src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7" style="display:none"')
      .replace(/src='cid:[^']*'/gi, "src='data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7' style='display:none'");
    
    return processedHtml;
  };

  // Auto-start time tracking for inbox context
  // Switch to inbox context when navigating to inbox (even if tracking a case)
  useEffect(() => {
    // Always switch to inbox context when entering inbox view
    // This ensures time tracking shows "Inbox" instead of a previous case
    if (currentContext !== 'inbox') {
      startContextTracking('inbox');
    }
  }, [currentContext, startContextTracking]);
  
  // WebSocket handlers (use refreshEmailsQuietly from hook)
  const handleWebSocketNewEmail = useCallback((data?: any) => {
    refreshEmailsQuietly();
  }, [refreshEmailsQuietly]);
  
  const handleWebSocketSyncComplete = useCallback((payload?: any) => {
    if (payload?.synced_count > 0) {
      refreshEmailsQuietly();
    }
  }, [refreshEmailsQuietly]);
  
  const handleWebSocketEmailAnalyzed = useCallback((payload?: any) => {
    if (payload?.message_id === selectedEmail?.id) {
      setSelectedEmail(prev => prev ? { ...prev, _analyzed: Date.now() } : null);
    }
  }, [selectedEmail?.id]);
  
  // Initialize WebSocket connection
  useEffect(() => {
    let ws = null;
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 5;
    let reconnectTimeout = null;
    let pingInterval = null;
    
    const connectWebSocket = () => {
      const token = auth.getToken();
      if (!token) {
        console.debug('WebSocket: No token available');
        return;
      }
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
      const wsHost = backendUrl.replace(/^https?:\/\//, '').replace(/\/api$/, '');
      const wsUrl = `${wsProtocol}://${wsHost}/api/ws/inbox?token=${token}`;
      
      try {
        ws = new WebSocket(wsUrl);
        
        ws.onopen = () => {
          console.log('✅ Inbox WebSocket connected');
          reconnectAttempts = 0; // Reset on successful connection
          
          // Start client-side ping to keep connection alive
          pingInterval = setInterval(() => {
            if (ws && ws.readyState === WebSocket.OPEN) {
              try {
                ws.send(JSON.stringify({ type: 'ping' }));
              } catch (e) {
                console.debug('WebSocket ping failed:', e);
              }
            }
          }, 30000); // Ping every 30 seconds
        };
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            switch (data.type) {
              case 'new_email':
                handleWebSocketNewEmail(data.data);
                break;
              case 'sync_completed':
                handleWebSocketSyncComplete(data.data);
                break;
              case 'email_analyzed':
                handleWebSocketEmailAnalyzed(data.data);
                break;
              case 'pong':
              case 'server_ping':
                // Heartbeat received, connection is alive
                break;
              default:
                break;
            }
          } catch (e) {
            console.debug('WebSocket message parse error:', e);
          }
        };
        
        ws.onclose = (event) => {
          console.log('WebSocket closed:', event.code, event.reason);
          
          // Clear ping interval
          if (pingInterval) {
            clearInterval(pingInterval);
            pingInterval = null;
          }
          
          // Attempt reconnect with exponential backoff if not intentional close
          if (event.code !== 1000 && reconnectAttempts < maxReconnectAttempts) {
            const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000); // Max 30 seconds
            console.log(`WebSocket reconnecting in ${delay/1000}s (attempt ${reconnectAttempts + 1}/${maxReconnectAttempts})`);
            reconnectAttempts++;
            reconnectTimeout = setTimeout(connectWebSocket, delay);
          } else if (reconnectAttempts >= maxReconnectAttempts) {
            console.log('WebSocket: Max reconnect attempts reached, using fallback polling');
          }
        };
        
        ws.onerror = (error) => {
          console.debug('WebSocket error - will use fallback polling');
        };
      } catch (e) {
        console.debug('WebSocket not available:', e);
      }
    };
    
    connectWebSocket();
    
    // Fallback: reduced polling as backup (every 60 seconds instead of 30)
    const fallbackRefresh = setInterval(() => {
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        refreshEmailsQuietly();
      }
    }, 60000);
    
    // Background sync every 3 minutes (reduced from 2)
    const syncInterval = setInterval(() => syncFromOutlookQuietly(), 180000);
    
    return () => {
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      if (pingInterval) {
        clearInterval(pingInterval);
      }
      if (ws) {
        ws.close(1000, 'Component unmounted');
      }
      clearInterval(fallbackRefresh);
      clearInterval(syncInterval);
    };
  }, [handleWebSocketNewEmail, handleWebSocketSyncComplete, handleWebSocketEmailAnalyzed]);

  // ===================
  // SELECTION HANDLERS
  // ===================

  const handleSelectEmail = async (email) => {
    userHasSelectedRef.current = true;
    setSelectedEmail(email);
    
    // Fetch full email content if body_html is not available
    if (email && !email.body_html) {
      try {
        const token = auth.getToken();
        
        // Check if this is a Gmail email
        if (email.source === 'google') {
          // Fetch full Gmail message content
          const res = await axios.get(`${API}/google/mail/messages/${email.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          // Merge Gmail response with existing email data
          setSelectedEmail(prev => ({
            ...prev,
            ...res.data,
            body_html: res.data.body_html || res.data.body_text,
            body: res.data.body_text || res.data.snippet,
            body_preview: res.data.snippet || res.data.body_preview,
            attachments: res.data.attachments || [],
            source: 'google'
          }));
        } else {
          // Fetch Outlook message content
          const res = await axios.get(`${API}/inbox/messages/${email.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setSelectedEmail(res.data);
        }
      } catch (error) {
        console.error('Error fetching email details:', error);
      }
    }
  };

  const handleSelectThread = (thread) => {
    userHasSelectedRef.current = true;
    setSelectedThread(thread);
  };

  const handleToggleExpand = (conversationId) => {
    setExpandedThread(prev => prev === conversationId ? null : conversationId);
  };

  // Wrap hook functions for component use
  const handleSync = () => syncFromOutlook(true);
  const handleMarkRead = (emailId) => markAsRead(emailId);
  const handleAssignToCase = (caseId) => assignToCase(selectedEmail?.id, caseId);

  // ===================
  // REPLY HANDLING (using hooks)
  // ===================

  const handleReply = (mode = 'reply') => {
    if (!selectedEmail) {
      toast.error('Velg en e-post først');
      return;
    }
    startReply(mode);
    setTimeout(() => {
      replyEditorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const handleCancelReply = () => cancelReply();
  const handleGenerateAIReply = () => generateAIReply(selectedEmail);
  const handleSendReply = () => sendReply(selectedEmail);

  // ===================
  // ATTACHMENTS (using hooks)
  // ===================

  const handlePreviewAttachment = (attachment) => previewAttachmentHandler(attachment, selectedEmail);
  const handleDownloadAttachment = (attachment) => downloadAttachment(attachment, selectedEmail);
  const handleClosePreview = () => closePreview();

  // ===================
  // RESIZE HANDLING
  // ===================

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isResizingH && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const newWidth = Math.max(280, Math.min(500, e.clientX - rect.left));
        setPanelWidth(newWidth);
      }
      if (isResizingDrawer && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const newWidth = Math.max(320, Math.min(600, rect.right - e.clientX));
        setAiDrawerWidth(newWidth);
      }
    };
    
    const handleMouseUp = () => {
      if (isResizingH) {
        setIsResizingH(false);
        saveToStorage(STORAGE_KEYS.PANEL_WIDTH, panelWidth);
      }
      if (isResizingDrawer) {
        setIsResizingDrawer(false);
        saveToStorage(STORAGE_KEYS.AI_DRAWER_WIDTH, aiDrawerWidth);
      }
    };
    
    if (isResizingH || isResizingDrawer) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizingH, isResizingDrawer, panelWidth, aiDrawerWidth]);

  // Auto-open AI drawer
  useEffect(() => {
    if (selectedEmail && !aiDrawerPinned) {
      setAiDrawerOpen(true);
    }
  }, [selectedEmail, aiDrawerPinned]);

  // ===================
  // COMPUTED
  // ===================

  const filteredThreads = filterEmails(threads, filter, searchQuery);
  const stats = calculateInboxStats(emails);

  // ===================
  // RENDER
  // ===================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-[#FEFEFE]">
        <div className="text-center">
          <Loader2 className="w-6 h-6 animate-spin text-gray-300 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">{t('inbox:loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="flex h-full bg-gray-50 relative overflow-hidden p-4 gap-4">
      {/* Email List Panel - White card with border */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" style={{ width: panelWidth, minWidth: 280, maxWidth: 400 }}>
        <EmailListPanel
        threads={filteredThreads}
        emails={emails}
        selectedEmail={selectedEmail}
        selectedThread={selectedThread}
        expandedThread={expandedThread}
        filter={filter}
        setFilter={setFilter}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        syncing={syncing}
        onSync={handleSync}
        onNewEmail={() => setComposeModalOpen(true)}
        onSelectEmail={(email) => {
          handleSelectEmail(email);
          const isUnread = !email?.read_by?.length && !email?.is_read;
          if (isUnread) handleMarkRead(email?.id);
        }}
        onSelectThread={handleSelectThread}
        onToggleExpand={handleToggleExpand}
        panelWidth={panelWidth}
        isResizing={isResizingH}
        onResizeStart={() => setIsResizingH(true)}
        outlookConnected={outlookStatus?.connected}
        stats={stats}
        triageView={triageView}
        onTriageViewChange={(view) => {
          setTriageView(view);
          userHasSelectedRef.current = false;
          setSelectedEmail(null);
          setSelectedThread(null);
        }}
        inboxTab={inboxTab}
        onInboxTabChange={(tab) => {
          setInboxTab(tab);
          userHasSelectedRef.current = false;
          setSelectedEmail(null);
          setSelectedThread(null);
        }}
        />
      </div>

      {/* Email Content Area - White card with border */}
      <div className="flex-1 flex flex-col bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        {selectedEmail ? (
          <>
            {/* Action Bar - Minimal */}
            <div className="flex-shrink-0 px-6 py-4 border-b border-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleReply('reply')}
                    className={cn(
                      "px-3 py-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors rounded-lg",
                      isReplying && replyMode === 'reply' && "bg-gray-100 text-gray-900"
                    )}
                  >
                    <Reply className="w-3.5 h-3.5 inline mr-1.5" />
                    {t('inbox:email.reply')}
                  </button>
                  <button
                    onClick={() => handleReply('replyAll')}
                    className={cn(
                      "px-3 py-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors rounded-lg",
                      isReplying && replyMode === 'replyAll' && "bg-gray-100 text-gray-900"
                    )}
                  >
                    <ReplyAll className="w-3.5 h-3.5 inline mr-1.5" />
                    {t('inbox:email.replyAll')}
                  </button>
                  <button
                    onClick={() => setMeetingModalOpen(true)}
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg"
                  >
                    <Calendar className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Case selector */}
                  <div className="relative">
                    <button
                      onClick={() => setShowCaseSelector(!showCaseSelector)}
                      className={cn(
                        "px-3 py-1.5 text-sm rounded-lg transition-colors flex items-center gap-1.5",
                        selectedEmail.case_id 
                          ? "text-gray-900 bg-gray-100" 
                          : "text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                      )}
                    >
                      <FolderPlus className="w-3.5 h-3.5" />
                      <ChevronDown className="w-3 h-3" />
                    </button>
                    
                    {showCaseSelector && (
                      <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50">
                        <div className="px-4 py-2 text-[10px] font-medium text-gray-400 uppercase tracking-wider">{t('inbox:selectCase')}</div>
                        <div className="max-h-56 overflow-y-auto">
                          {cases?.slice(0, 10).map(c => (
                            <button
                              key={c.id}
                              onClick={() => handleAssignToCase(c.id)}
                              disabled={isAssigning}
                              className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center gap-3 transition-colors"
                            >
                              <div className="w-0.5 h-5 rounded-full bg-gray-200" />
                              <span className="truncate text-gray-700">{c.topic || c.title}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <button
                    onClick={() => setAiDrawerOpen(!aiDrawerOpen)}
                    className={cn(
                      "p-2 rounded-lg transition-colors",
                      aiDrawerOpen 
                        ? "bg-gray-900 text-white" 
                        : "text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                    )}
                  >
                    {aiDrawerOpen ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Reply Editor - World-Class Design */}
            {isReplying && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                ref={replyEditorRef}
                className="flex-shrink-0 bg-white border-t border-gray-100"
              >
                {/* Compact Header */}
                <div className="px-6 pt-4 pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-gray-400">{t('inbox:email.to')}</span>
                      <span className="text-sm font-medium text-gray-900">
                        {selectedEmail.from?.name || selectedEmail.from?.email}
                      </span>
                    </div>
                    <button 
                      onClick={handleCancelReply}
                      className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1 truncate">
                    Re: {selectedEmail.subject || `(${t('inbox:email.noSubject')})`}
                  </p>
                </div>

                {/* Rich Text Editor */}
                <div className="px-6">
                  <div className="rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm hover:shadow transition-shadow">
                    <ReactQuill
                      theme="snow"
                      value={replyBody}
                      onChange={setReplyBody}
                      placeholder={t('inbox:reply.placeholder')}
                      modules={{
                        toolbar: [
                          ['bold', 'italic', 'underline'],
                          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                          ['link'],
                          ['clean']
                        ]
                      }}
                      formats={['bold', 'italic', 'underline', 'list', 'bullet', 'link']}
                      className="reply-editor-premium"
                    />
                  </div>
                </div>

                {/* Action Bar - Premium Design */}
                <div className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    {/* Tone Selection - Pill Style */}
                    <div className="flex items-center gap-1 p-1 bg-gray-100/80 rounded-xl">
                      {['professional', 'friendly', 'formal', 'brief'].map(tone => (
                        <button
                          key={tone}
                          onClick={() => setReplyTone(tone)}
                          className={cn(
                            "text-[11px] px-3 py-1.5 rounded-lg transition-all font-medium",
                            replyTone === tone 
                              ? 'bg-white text-gray-900 shadow-sm' 
                              : 'text-gray-500 hover:text-gray-700'
                          )}
                        >
                          {t(`inbox:tone.${tone}`)}
                        </button>
                      ))}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={handleGenerateAIReply} 
                        disabled={generatingAI} 
                        className="h-9 px-4 text-xs font-medium text-gray-600 hover:text-gray-900 bg-gray-100/80 hover:bg-gray-200/80 rounded-xl transition-all flex items-center gap-2"
                      >
                        {generatingAI ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Sparkles className="w-3.5 h-3.5" />
                        )}
                        {t('inbox:reply.generateWithAI')}
                      </button>
                      
                      <button 
                        onClick={handleSendReply} 
                        disabled={sendingReply || !replyBody.trim()} 
                        className="h-9 px-5 text-xs font-medium bg-gray-900 hover:bg-gray-800 text-white rounded-xl disabled:opacity-40 transition-all flex items-center gap-2 shadow-sm hover:shadow"
                      >
                        {sendingReply ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Send className="w-3.5 h-3.5" />
                        )}
                        {t('inbox:reply.send')}
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Email Header - Clean minimalist */}
            <div className="flex-shrink-0 px-6 py-5 border-b border-gray-50">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {selectedEmail.subject || `(${t('inbox:email.noSubject')})`}
              </h2>
              <div className="flex items-center gap-4">
                <div className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm",
                  selectedEmail.direction === 'OUTBOUND' ? "bg-gray-400" : "bg-gray-900"
                )}>
                  {selectedEmail.direction === 'OUTBOUND' ? (
                    <Send className="w-4 h-4" />
                  ) : (
                    (selectedEmail.from?.name?.[0] || '?').toUpperCase()
                  )}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900 text-sm">
                    {selectedEmail.direction === 'OUTBOUND'
                      ? `${t('inbox:email.to')}: ${selectedEmail.to?.[0]?.name || selectedEmail.to?.[0]?.email}`
                      : selectedEmail.from?.name || selectedEmail.from?.email
                    }
                  </p>
                  <p className="text-xs text-gray-400 flex items-center gap-1.5 mt-0.5">
                    {formatEmailTime(selectedEmail.created_at)}
                  </p>
                </div>
              </div>
            </div>

            {/* Attachments - Minimal */}
            {selectedEmail.attachments?.length > 0 && (
              <div className="flex-shrink-0 px-6 py-3 border-b border-gray-50">
                <div className="flex items-center gap-2 flex-wrap">
                  <Paperclip className="w-3.5 h-3.5 text-gray-300" />
                  {selectedEmail.attachments.map((att, idx) => (
                    <button
                      key={att.id || idx}
                      onClick={() => handlePreviewAttachment(att)}
                      className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg text-sm text-gray-600 hover:bg-gray-100 transition-colors"
                    >
                      <FileText className="w-3.5 h-3.5 text-gray-400" />
                      <span className="truncate max-w-[120px]">{att.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Email Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {selectedEmail.body_html ? (
                <iframe
                  key={`${selectedEmail.id}-${Object.keys(inlineImages).length}`}
                  srcDoc={`
                    <!DOCTYPE html>
                    <html>
                    <head>
                      <style>
                        body { 
                          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                          font-size: 14px;
                          line-height: 1.7;
                          color: #374151;
                          margin: 0;
                          padding: 0;
                          word-wrap: break-word;
                        }
                        ol, ul {
                          margin: 0.5em 0;
                          padding-left: 2em;
                        }
                        ol { list-style-type: decimal; }
                        ul { list-style-type: disc; }
                        li { margin: 0.25em 0; display: list-item; }
                        table { max-width: 100%; border-collapse: collapse; }
                        td, th { padding: 2px 4px; vertical-align: top; }
                        img { max-width: 100%; height: auto; }
                        a { color: #111827; text-decoration: underline; }
                        a:hover { color: #374151; }
                        blockquote {
                          margin: 1em 0;
                          padding-left: 1em;
                          border-left: 2px solid #e5e7eb;
                          color: #6b7280;
                        }
                        hr { border: none; border-top: 1px solid #f3f4f6; margin: 1em 0; }
                        .gmail_signature, #Signature, .signature {
                          margin-top: 1em;
                          padding-top: 0.5em;
                          border-top: 1px solid #f3f4f6;
                          color: #9ca3af;
                          font-size: 12px;
                        }
                        pre, code {
                          font-family: 'SF Mono', Monaco, 'Courier New', monospace;
                          font-size: 13px;
                          background: #f9fafb;
                          border-radius: 6px;
                        }
                        pre { padding: 12px; overflow-x: auto; }
                        code { padding: 2px 6px; }
                      </style>
                    </head>
                    <body>${processEmailHtml(selectedEmail.body_html)}</body>
                    </html>
                  `}
                  className="w-full h-full border-0"
                  sandbox="allow-same-origin"
                  title="Email content"
                />
              ) : (
                <div className="whitespace-pre-wrap text-gray-600 leading-relaxed text-sm">
                  {selectedEmail.body || selectedEmail.body_preview || t('inbox:noContent')}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center">
            <Inbox className="w-12 h-12 mb-3 text-gray-200" />
            <p className="text-sm font-medium text-gray-500 mb-1">{t('inbox:selectEmail')}</p>
            <p className="text-xs text-gray-400">{t('inbox:selectEmailDescription')}</p>
          </div>
        )}
      </div>

      {/* AI Drawer - Clean Minimalist */}
      <AnimatePresence>
        {aiDrawerOpen && selectedEmail && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: aiDrawerWidth, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            className="flex-shrink-0 bg-[#F9F9F8] rounded-2xl border border-gray-200 shadow-sm overflow-hidden relative"
            style={{ width: aiDrawerWidth }}
          >
            <div
              onMouseDown={() => setIsResizingDrawer(true)}
              className={cn(
                "absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-gray-200 transition-colors",
                isResizingDrawer && "bg-gray-200"
              )}
            />
            <InboxTriagePanelV2
              email={selectedEmail}
              isOpen={true}
              cases={cases}
              width={aiDrawerWidth}
              onClose={() => setAiDrawerOpen(false)}
              onAssignCase={(caseId) => handleAssignToCase(caseId)}
              onNavigateToCase={onNavigateToCase || ((caseId) => navigate(`/lexy/cases/${caseId}`))}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modals */}
      <EmailComposeModal
        isOpen={composeModalOpen}
        onClose={() => setComposeModalOpen(false)}
        onSent={() => { setComposeModalOpen(false); fetchData(); }}
      />
      
      <MeetingBookingModal
        isOpen={meetingModalOpen}
        onClose={() => setMeetingModalOpen(false)}
        email={selectedEmail}
        onBooked={() => { setMeetingModalOpen(false); toast.success('Møte booket!'); }}
      />

      {/* Attachment Preview Modal - Clean Minimal */}
      <AnimatePresence>
        {previewAttachment && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-6"
            onClick={handleClosePreview}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-5 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4 text-gray-400" />
                  <span className="font-medium text-gray-900 text-sm">{previewAttachment.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => handleDownloadAttachment(previewAttachment)} 
                    className="text-sm text-gray-500 hover:text-gray-900 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" /> Last ned
                  </button>
                  <button 
                    onClick={handleClosePreview} 
                    className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="p-5 max-h-[calc(90vh-80px)] overflow-auto bg-gray-50/50">
                {loadingAttachment ? (
                  <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-300" />
                  </div>
                ) : attachmentContent ? (
                  <>
                    {/* Image preview */}
                    {attachmentContent.type?.startsWith('image/') && attachmentContent.url && (
                      <img src={attachmentContent.url} alt={attachmentContent.name} className="max-w-full mx-auto rounded-xl" />
                    )}
                    
                    {/* PDF preview - using iframe with controls */}
                    {attachmentContent.type?.includes('pdf') && attachmentContent.url && (
                      <div className="flex flex-col h-[70vh]">
                        {/* PDF controls */}
                        <div className="flex justify-end gap-2 mb-2">
                          <a
                            href={attachmentContent.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2 text-gray-700"
                          >
                            <ExternalLink className="w-4 h-4" />
                            Åpne i ny fane
                          </a>
                        </div>
                        {/* PDF iframe */}
                        <iframe
                          src={attachmentContent.url}
                          className="w-full flex-1 rounded-xl bg-white border border-gray-200"
                          title={attachmentContent.name}
                        />
                      </div>
                    )}
                    
                    {/* DOCX preview */}
                    {docxHtml && (
                      <div 
                        className="bg-white p-8 rounded-xl shadow-sm max-w-4xl mx-auto"
                        style={{
                          fontFamily: "'Times New Roman', Times, serif",
                          fontSize: '12pt',
                          lineHeight: '1.5',
                          color: '#000'
                        }}
                      >
                        <style>{`
                          .docx-content strong, .docx-content b { font-weight: 700 !important; }
                          .docx-content em, .docx-content i { font-style: italic !important; }
                          .docx-content u { text-decoration: underline !important; }
                          .docx-content h1 { font-size: 18pt; font-weight: 700; margin: 24px 0 12px 0; }
                          .docx-content h2 { font-size: 14pt; font-weight: 700; margin: 20px 0 10px 0; }
                          .docx-content h3 { font-size: 12pt; font-weight: 700; margin: 16px 0 8px 0; }
                          .docx-content p { margin: 0 0 12px 0; }
                          .docx-content ul, .docx-content ol { margin: 12px 0; padding-left: 24px; }
                          .docx-content li { margin: 4px 0; }
                          .docx-content table { border-collapse: collapse; width: 100%; margin: 12px 0; }
                          .docx-content td, .docx-content th { border: 1px solid #000; padding: 6px 8px; }
                        `}</style>
                        <div className="docx-content" dangerouslySetInnerHTML={{ __html: docxHtml }} />
                      </div>
                    )}
                    
                    {/* Unsupported format - show download option */}
                    {!attachmentContent.url && !docxHtml && (
                      <div className="flex flex-col items-center justify-center py-20">
                        <FileText className="w-16 h-16 text-gray-300 mb-4" />
                        <p className="text-gray-600 mb-2">Forhåndsvisning ikke tilgjengelig</p>
                        <p className="text-gray-400 text-sm mb-4">Denne filtypen kan ikke vises i nettleseren</p>
                        <button 
                          onClick={() => handleDownloadAttachment(previewAttachment)}
                          className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 flex items-center gap-2"
                        >
                          <Download className="w-4 h-4" />
                          Last ned fil
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-20 text-gray-400 text-sm">Kunne ikke laste vedlegg</div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SplitInboxView;
